import { Controller } from "@hotwired/stimulus"
import { post } from "@rails/request.js"
import YouTubePlayer from "youtube-player"
import * as PlayerLogic from "../lib/player_logic"

// The single video player for both contexts (see
// docs/features/v1/video-player.md). Segment bounds present vs. absent is the
// only input difference:
//   - lesson context  (trackingMode 'lesson'): segment-aware course player —
//     custom chrome, segment capping, resume + LessonProgress writes.
//   - watch context    (trackingMode 'playback'): full video, no resume,
//     VideoPlayback analytics (mirrors the legacy video_controller).
// Both run native YouTube controls (controls: 1) plus our chrome — see
// initializePlayer for why.
// All decision logic lives in lib/player_logic.js (unit-tested in
// spec/javascript/player_logic.test.js) so this consolidation is guarded.
export default class extends Controller {
  static values = {
    courseUuid: String,
    lessonId: Number,
    videoIdentifier: String,
    videoDbId: Number,
    lastPosition: Number,
    nextLessonUrl: String,
    currentLessonIntroViewed: Boolean,
    startTime: { type: Number, default: 0 },
    endTime: { type: Number, default: 0 },
    segmentMode: { type: Boolean, default: true },
    videoDuration: { type: Number, default: 0 },
    trackingMode: { type: String, default: 'lesson' },
    searchAttribution: String,
    // Phase 3 — Netflix resume: per-(student, video) last position + when it was
    // saved (epoch ms; 0 = none). Both contexts read these.
    resumePosition: { type: Number, default: 0 },
    resumeWatchedAt: { type: Number, default: 0 }
  }

  static targets = ["stage", "speedButton", "playPauseButton", "playIcon", "pauseIcon", "stepForwardButton", "stepBackButton", "currentSpeedDisplay", "scrubber", "scrubberFill", "scrubberThumb", "scrubberPreDisabled", "scrubberPostDisabled", "timeStart", "timeEnd", "resumeOverlay", "resumeButton", "startButton", "resumeTime", "segmentRow", "poster", "endOverlay"]

  initialize() {
    this.progressInterval = null
    this.scrubberInterval = null
    this.isDragging = false
    this.scrubberDragMoveBound = null
    this.scrubberDragEndBound = null
    this.scrubberTouchStartBound = null
    this.playbackSpeed = 1.0
    this.hasResumed = false
    this.isPlaying = false
    this.frameStep = 0.033 // Approximate 1 frame at 30fps
    this.playerInitialized = false
    // iOS muted-autoplay: set when we start muted so PLAYING knows to unmute.
    this.unmuteWhenPlaying = false
    // Watch-context (playback mode) analytics state
    this.current = {}
    this.lastStopTime = 0
    this.relatedLessonId = undefined
  }

  connect() {
    // Initialise the player. The lesson context gates on the intro having been
    // viewed; the standalone watch and preview contexts have no intro, so init
    // immediately.
    if (this.isPlaybackMode() || this.isPreviewMode() || this.currentLessonIntroViewedValue) {
      this.initializePlayer()
    }

    // Handle page unload
    this.handleWindowUnload = this.handleWindowUnload.bind(this)
    window.addEventListener('beforeunload', this.handleWindowUnload)

    // Listen for replay / watch-full events from the interstitial (lesson context)
    this.handleReplayBound = this.handleReplay.bind(this)
    this.element.addEventListener('lesson-interstitial:replay', this.handleReplayBound)
    this.handleWatchFullBound = this.handleWatchFull.bind(this)
    this.element.addEventListener('lesson-interstitial:watchFull', this.handleWatchFullBound)

    // Add touch listener for scrubber with { passive: false } so preventDefault() works
    if (this.hasScrubberTarget) {
      this.scrubberTouchStartBound = this.scrubberTouchStart.bind(this)
      this.scrubberTarget.addEventListener('touchstart', this.scrubberTouchStartBound, { passive: false })
    }

    // Populate time labels immediately using values available from the DOM
    this.updateScrubberLabels()
    this.updateCurrentTimeLabel(this.startTimeValue || 0)

    // Reflect the default 1x in the speed control before any interaction.
    this.updateSpeedDisplay()
  }

  initializePlayer() {
    if (this.playerInitialized) return
    this.playerInitialized = true

    // Both contexts run native YouTube controls (controls: 1) alongside our own
    // chrome. The native scrubber is kept for its storyboard hover-preview (the
    // little thumbnail filmstrip as you drag), which the IFrame API doesn't
    // expose and so can't be replicated; our chrome adds what native lacks
    // (frame-step, segment hot-links, large mat-friendly tap targets). Showing
    // native controls/branding is also the compliant default.
    const playerVars = (this.isPlaybackMode() || this.isPreviewMode())
      ? { autoplay: 0, controls: 1, modestbranding: 1, rel: 0, iv_load_policy: 3, playsinline: 1 }
      : { autoplay: 0, controls: 1, modestbranding: 1, rel: 0, iv_load_policy: 3 }

    const player = YouTubePlayer(this.stageTarget, {
      width: '100%',
      height: '100%',
      playerVars
    })

    player.on('ready', e => {
      this.youtube = e.target
    })

    player.on('stateChange', e => {
      const time = e.target.playerInfo.currentTime

      // Watch context: analytics + our custom chrome (no course progress / no
      // segment capping / no completion interstitial).
      if (this.isPlaybackMode()) {
        this.handlePlaybackStateChange(e, time)
        this.handlePlaybackChrome(e, time)
        return
      }

      // Preview context (onboarding reveal): drive the chrome + segment capping
      // like watch mode, but with no analytics — handlePlaybackStateChange is
      // skipped, and updateProgress is a no-op here (see below).
      if (this.isPreviewMode()) {
        this.handlePlaybackChrome(e, time)
        return
      }

      // --- Lesson context ---

      // VIDEO_CUED — video loaded but not yet playing; duration is available
      if (e.data === 5) {
        this.tryInitScrubber()
      }

      // PLAYING
      if (e.data === 1) {
        this.isPlaying = true
        this.updatePlayPauseButton()
        this.startProgressTracking()
        // Try to init scrubber duration on first play (fallback if VIDEO_CUED didn't fire)
        if (!this.videoDurationValue) {
          this.tryInitScrubber()
        }
      }

      // PAUSED
      if (e.data === 2) {
        this.isPlaying = false
        this.updatePlayPauseButton()
        this.stopProgressTracking()
        this.updateProgress(time)
        this.updateScrubber(time)
        // Enforce segment boundaries on seek-while-paused via native controls
        const correctedTime = PlayerLogic.seekWhilePausedTarget(time, this.startTimeValue, this.endTimeValue, this.isSegmentMode())
        if (correctedTime !== null) {
          this.youtube.seekTo(correctedTime, true)
        }
      }

      // ENDED
      if (e.data === 0) {
        this.isPlaying = false
        this.updatePlayPauseButton()
        this.stopProgressTracking()
        this.updateProgress(time)

        // Show completion interstitial instead of auto-advancing
        this.showCompletionInterstitial()
      }
    })

    // Preview context (onboarding reveal): autoplay the clip at its segment start
    // as soon as it's cloned into the slide-up sheet. Only one preview player is
    // instantiated at a time (the pick's template is cloned on open), so there is
    // no competing-clip concern — mirror the watch path exactly. loadVideoById
    // both loads and plays; on iOS the play is async to the tap, so start muted
    // and lift the mute on PLAYING (see handlePlaybackChrome). The scrubber-poll
    // boundary check still caps playback at endTime, and the poster tap remains a
    // fallback if autoplay is blocked (e.g. iOS Low Power Mode). No resume.
    if (this.isPreviewMode()) {
      if (PlayerLogic.isIOS(navigator.userAgent, navigator.maxTouchPoints)) {
        this.unmuteWhenPlaying = true
        player.mute()
      }
      player.loadVideoById({ videoId: this.videoIdentifierValue, startSeconds: this.startTimeValue || 0 })
      return
    }

    // Phase 3 — Netflix resume choice (both contexts). Decide whether to offer
    // Resume vs. Start; the choice doubles as the click-to-play gesture.
    this.resume = PlayerLogic.resumeDecision({
      savedPosition: this.resumePositionValue,
      savedWatchedAtMs: this.resumeWatchedAtValue || null,
      nowMs: Date.now(),
      startTime: this.startTimeValue,
      endTime: this.endTimeValue,
      segment: this.isSegmentMode(),
      duration: this.videoDurationValue
    })

    const loadOptions = { videoId: this.videoIdentifierValue }
    if (this.resume.startSeconds > 0) {
      loadOptions.startSeconds = this.resume.startSeconds
    }

    if (this.resume.offer) {
      // Cue (no autoplay) and let the overlay drive the first play from the
      // student's choice — keeps us click-to-play and §4-clean.
      player.cueVideoById(loadOptions)
      this.showResumeOverlay()
    } else {
      // No meaningful resume: autoplay on open (the student tapped the thumbnail,
      // mirroring what YouTube itself does). loadVideoById both loads and plays.
      //
      // iOS blocks autoplay-with-sound even right after that tap, because the
      // play() here is async (it runs after the popover's fetch resolves), so the
      // gesture no longer counts. Muted autoplay IS allowed, so on iOS we start
      // muted and lift the mute once playback is actually under way (see the
      // PLAYING branch in handlePlaybackChrome). Other platforms autoplay with
      // sound as before. Lesson context is untouched.
      if (this.isPlaybackMode() && PlayerLogic.isIOS(navigator.userAgent, navigator.maxTouchPoints)) {
        this.unmuteWhenPlaying = true
        player.mute()
      }
      player.loadVideoById(loadOptions)
    }
  }

  // Remove the loading poster once playback actually starts. Kept until PLAYING
  // (not the iframe's `load` event) so YouTube's own loading state is never seen.
  hidePoster() {
    if (this.hasPosterTarget) this.posterTarget.remove()
  }

  // Tapping the poster starts playback. With muted autoplay we usually never get
  // here, but it remains a direct-user-gesture fallback if autoplay is still
  // blocked (e.g. Low Power Mode on iOS). The poster is removed when PLAYING fires.
  posterPlay(event) {
    event.preventDefault()
    if (!this.youtube) return
    try {
      this.youtube.playVideo()
    } catch (error) {
      console.error('Error starting playback from poster:', error)
    }
  }

  // --- Resume overlay (Phase 3) -------------------------------------------

  showResumeOverlay() {
    if (!this.hasResumeOverlayTarget) return
    if (this.hasResumeTimeTarget) {
      this.resumeTimeTarget.textContent = this.formatTime(this.resume.resumeSeconds)
    }
    this.applyResumeEmphasis(this.resume.default)
    this.resumeOverlayTarget.classList.remove('hidden')
    this.resumeOverlayTarget.classList.add('flex')
  }

  hideResumeOverlay() {
    if (!this.hasResumeOverlayTarget) return
    this.resumeOverlayTarget.classList.add('hidden')
    this.resumeOverlayTarget.classList.remove('flex')
  }

  // Emphasise the default choice (stale ⇒ 'start' is primary, Resume still shown).
  applyResumeEmphasis(defaultChoice) {
    if (!this.hasResumeButtonTarget || !this.hasStartButtonTarget) return
    const primary = ['bg-amber-500', 'text-black', 'font-semibold', 'order-1']
    const secondary = ['bg-transparent', 'text-white/80', 'underline', 'font-normal', 'order-2']
    const setPrimary = el => { el.classList.remove(...secondary); el.classList.add(...primary) }
    const setSecondary = el => { el.classList.remove(...primary); el.classList.add(...secondary) }
    if (defaultChoice === 'start') {
      setPrimary(this.startButtonTarget)
      setSecondary(this.resumeButtonTarget)
    } else {
      setPrimary(this.resumeButtonTarget)
      setSecondary(this.startButtonTarget)
    }
  }

  resumeChoice(event) {
    event.preventDefault()
    this.hideResumeOverlay()
    if (!this.youtube) return
    this.updateActiveSegment(this.resume.resumeSeconds)
    this.youtube.seekTo(this.resume.resumeSeconds, true)
    this.youtube.playVideo()
  }

  startChoice(event) {
    event.preventDefault()
    this.hideResumeOverlay()
    this.clearSavedResume()
    if (!this.youtube) return
    this.updateActiveSegment(this.resume.startSeconds)
    this.youtube.seekTo(this.resume.startSeconds, true)
    this.youtube.playVideo()
  }

  // Persist the start-over choice immediately so a quick refresh doesn't re-offer
  // the position the student just skipped. Course context zeroes the lesson's
  // LessonProgress position (completed_at, and so course completion, preserved);
  // watch context zeroes the per-(student, video) VideoResumePosition.
  clearSavedResume() {
    this.resumePositionValue = 0
    const body = this.isPlaybackMode()
      ? { video_id: this.videoDbIdValue }
      : { course_uuid: this.courseUuidValue, lesson_id: this.lessonIdValue }
    post('/video_resume_positions/clear', { body, responseKind: 'json' })
  }

  disconnect() {
    this.stopProgressTracking()
    if (this.handleWindowUnload) {
      window.removeEventListener('beforeunload', this.handleWindowUnload)
    }
    if (this.handleReplayBound) {
      this.element.removeEventListener('lesson-interstitial:replay', this.handleReplayBound)
    }
    if (this.handleWatchFullBound) {
      this.element.removeEventListener('lesson-interstitial:watchFull', this.handleWatchFullBound)
    }
    if (this.scrubberTouchStartBound && this.hasScrubberTarget) {
      this.scrubberTarget.removeEventListener('touchstart', this.scrubberTouchStartBound)
    }
    // Remove any active drag listeners
    if (this.scrubberDragMoveBound) {
      document.removeEventListener('mousemove', this.scrubberDragMoveBound)
      document.removeEventListener('touchmove', this.scrubberDragMoveBound)
    }
    if (this.scrubberDragEndBound) {
      document.removeEventListener('mouseup', this.scrubberDragEndBound)
      document.removeEventListener('touchend', this.scrubberDragEndBound)
    }
  }

  isPlaybackMode() {
    return this.trackingModeValue === 'playback'
  }

  // Onboarding reveal: a bounded, click-to-play clip with no course-progress or
  // analytics writes (the visitor is anonymous). Segment capping still applies.
  isPreviewMode() {
    return this.trackingModeValue === 'preview'
  }

  // --- Watch-context analytics (mirrors the legacy video_controller) ---

  handlePlaybackStateChange(e, time) {
    // PLAYING
    if (e.data === 1) {
      const startTime = PlayerLogic.resolvePlayStartTime(time, this.lastStopTime)
      this.current = { video_id: this.videoDbIdValue, start_time: PlayerLogic.toTenth(startTime) }
      if (this.hasSearchAttributionValue) this.current.search_attribution = this.searchAttributionValue
    }

    // PAUSED or ENDED
    if (e.data === 2 || e.data === 0) {
      this.current.end_time = PlayerLogic.toTenth(time)
      this.lastStopTime = time
      this.fireEvent()
    }
  }

  // Drive the custom chrome (play/pause button, scrubber, segment highlight) in
  // playback mode. No course progress POST, no segment capping, no interstitial.
  handlePlaybackChrome(e, time) {
    if (e.data === 5) this.tryInitScrubber() // VIDEO_CUED — duration available

    if (e.data === 1) { // PLAYING
      this.isPlaying = true
      this.updatePlayPauseButton()
      this.startScrubberPolling()
      this.hidePoster() // real video frames are showing now
      this.hideEndOverlay()
      // iOS muted-autoplay: now that playback is established, lift the mute. A
      // short delay lets the first frames settle — unmuting on the very first
      // PLAYING tick can re-trip iOS's autoplay gate and silently re-mute.
      if (this.unmuteWhenPlaying) {
        this.unmuteWhenPlaying = false
        setTimeout(() => {
          try { if (this.youtube) this.youtube.unMute() } catch (error) { /* player gone */ }
        }, 100)
      }
      if (!this.videoDurationValue) this.tryInitScrubber()
    }

    if (e.data === 2 || e.data === 0) { // PAUSED or ENDED
      this.isPlaying = false
      this.updatePlayPauseButton()
      this.stopProgressTracking()
      this.updateScrubber(time)
    }

    if (e.data === 0) this.showEndOverlay()
  }

  showEndOverlay() {
    if (this.hasEndOverlayTarget) this.endOverlayTarget.classList.remove('hidden')
  }

  hideEndOverlay() {
    if (this.hasEndOverlayTarget) this.endOverlayTarget.classList.add('hidden')
  }

  replay() {
    if (!this.youtube) return

    this.hideEndOverlay()
    this.youtube.seekTo(this.startTimeValue || 0, true)
    this.youtube.playVideo()
  }

  showRelated() {
    this.hideEndOverlay()
    window.dispatchEvent(new CustomEvent('player-tabs:open', { detail: { tab: 'related' } }))
  }

  fireEvent() {
    post(document.body.dataset.playbacksUrl, {
      responseKind: 'json',
      body: this.current
    })
  }

  handlePlaybackUnload() {
    // Save the in-flight playback range if the player is still playing.
    if (this.youtube && this.current && this.current.start_time !== undefined) {
      const playerState = this.youtube.getPlayerState()
      // YouTube player state 1 = playing
      if (playerState === 1) {
        const currentTime = this.youtube.getCurrentTime()
        this.current.end_time = PlayerLogic.toTenth(currentTime)

        // Use sendBeacon for reliable delivery during page unload
        if (navigator.sendBeacon) {
          const formData = new FormData()
          formData.append('video_id', this.current.video_id)
          formData.append('start_time', this.current.start_time)
          formData.append('end_time', this.current.end_time)
          if (this.current.search_attribution) {
            formData.append('search_attribution', this.current.search_attribution)
          }

          const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content')
          if (csrfToken) {
            formData.append('authenticity_token', csrfToken)
          }

          navigator.sendBeacon(document.body.dataset.playbacksUrl, formData)
        } else {
          this.fireEvent()
        }
      }
    }
  }

  startProgressTracking() {
    // Course-context only: report progress to update_progress every 10 seconds.
    if (!this.progressInterval) {
      this.progressInterval = setInterval(() => {
        if (this.youtube && this.youtube.getCurrentTime) {
          try {
            // YouTube iframe API getCurrentTime() is synchronous
            const time = this.youtube.getCurrentTime()
            if (typeof time === 'number') {
              this.updateProgress(time)
            }
          } catch (error) {
            console.warn('Error tracking progress:', error)
          }
        }
      }, 10000) // 10 seconds
    }

    this.startScrubberPolling()
  }

  // Scrubber/segment UI polling (both contexts). In playback mode the video is
  // full (isSegmentMode false), so the boundary action is always 'none' — no
  // segment capping or completion interstitial fires.
  startScrubberPolling() {
    // Poll scrubber and end-time boundary at 100ms
    if (!this.scrubberInterval) {
      this.scrubberInterval = setInterval(() => {
        if (this.youtube && this.youtube.getCurrentTime) {
          try {
            const current = this.youtube.getCurrentTime()
            if (typeof current === 'number') {
              this.updateScrubber(current)
              const action = PlayerLogic.playbackBoundaryAction(current, this.startTimeValue, this.endTimeValue, this.isSegmentMode())
              if (action.type === 'seek') {
                this.youtube.seekTo(action.target, true)
              } else if (action.type === 'end') {
                this.stopProgressTracking()
                this.youtube.pauseVideo()
                this.updateProgress(this.endTimeValue)
                this.showCompletionInterstitial()
              }
            }
          } catch (error) {
            console.warn('Error in scrubber interval:', error)
          }
        }
      }, 100)
    }
  }

  stopProgressTracking() {
    if (this.progressInterval) {
      clearInterval(this.progressInterval)
      this.progressInterval = null
    }
    if (this.scrubberInterval) {
      clearInterval(this.scrubberInterval)
      this.scrubberInterval = null
    }
  }

  // --- Scrubber helpers ---

  isSegmentMode() {
    return PlayerLogic.isSegmentMode(this.segmentModeValue, this.endTimeValue)
  }

  tryInitScrubber() {
    if (!this.youtube || !this.youtube.getDuration) return
    try {
      const duration = this.youtube.getDuration()
      if (typeof duration === 'number' && duration > 0) {
        this.videoDurationValue = duration
        this.updateScrubberLabels()
      }
    } catch (error) {
      console.warn('Error getting video duration:', error)
    }
  }

  updateScrubberLabels() {
    if (this.hasTimeEndTarget) {
      this.timeEndTarget.textContent = this.formatTime(this.videoDurationValue)
    }
    this.updateDisabledZones()
  }

  updateCurrentTimeLabel(time) {
    if (this.hasTimeStartTarget) {
      this.timeStartTarget.textContent = this.formatTime(time)
    }
  }

  updateScrubber(currentTime) {
    // Skip redundant DOM writes when nothing visible changed (same tenth of a
    // second). Avoids needless reflow every 100ms during steady playback, and
    // keeps the DOM quiet when the time isn't advancing.
    const rounded = Math.round(currentTime * 10)
    if (rounded === this._lastScrubTenth) return
    this._lastScrubTenth = rounded

    const position = PlayerLogic.scrubberPosition(currentTime, this.videoDurationValue)
    this.updateScrubberPositionUI(position)
    this.updateCurrentTimeLabel(currentTime)
    this.updateActiveSegment(currentTime)
  }

  updateScrubberPositionUI(position) {
    const pct = PlayerLogic.pctString(position)
    if (this.hasScrubberFillTarget) {
      this.scrubberFillTarget.style.width = pct
    }
    if (this.hasScrubberThumbTarget) {
      this.scrubberThumbTarget.style.left = pct
    }
  }

  scrubberMouseDown(event) {
    event.preventDefault()
    this.isDragging = true
    this.wasPlayingBeforeDrag = this.isPlaying
    if (this.isPlaying) this.youtube.pauseVideo()
    const position = this.positionFromEvent(event)
    this.updateScrubberPositionUI(position)
    this.seekToPosition(position)
    this.scrubberDragMoveBound = this.scrubberDragMove.bind(this)
    this.scrubberDragEndBound = this.scrubberDragEnd.bind(this)
    document.addEventListener('mousemove', this.scrubberDragMoveBound)
    document.addEventListener('mouseup', this.scrubberDragEndBound)
  }

  scrubberTouchStart(event) {
    event.preventDefault()
    this.isDragging = true
    this.wasPlayingBeforeDrag = this.isPlaying
    if (this.isPlaying) this.youtube.pauseVideo()
    const position = this.positionFromEvent(event)
    this.updateScrubberPositionUI(position)
    this.seekToPosition(position)
    this.scrubberDragMoveBound = this.scrubberDragMove.bind(this)
    this.scrubberDragEndBound = this.scrubberDragEnd.bind(this)
    document.addEventListener('touchmove', this.scrubberDragMoveBound, { passive: false })
    document.addEventListener('touchend', this.scrubberDragEndBound)
  }

  scrubberDragMove(event) {
    if (!this.isDragging) return
    event.preventDefault()
    const position = this.positionFromEvent(event)
    this.updateScrubberPositionUI(position)
    this.seekToPosition(position)
  }

  scrubberDragEnd(event) {
    if (!this.isDragging) return
    this.isDragging = false
    const position = this.positionFromEvent(event)
    this.updateScrubberPositionUI(position)
    this.seekToPosition(position)
    if (this.wasPlayingBeforeDrag) this.youtube.playVideo()
    document.removeEventListener('mousemove', this.scrubberDragMoveBound)
    document.removeEventListener('mouseup', this.scrubberDragEndBound)
    document.removeEventListener('touchmove', this.scrubberDragMoveBound)
    document.removeEventListener('touchend', this.scrubberDragEndBound)
    this.scrubberDragMoveBound = null
    this.scrubberDragEndBound = null
  }

  positionFromEvent(event) {
    if (!this.hasScrubberTarget) return 0
    const rect = this.scrubberTarget.getBoundingClientRect()
    let clientX
    if (event.touches && event.touches.length > 0) {
      clientX = event.touches[0].clientX
    } else if (event.changedTouches && event.changedTouches.length > 0) {
      clientX = event.changedTouches[0].clientX
    } else {
      clientX = event.clientX
    }
    return PlayerLogic.positionFromClientX(clientX, rect.left, rect.width)
  }

  seekToPosition(position) {
    if (!this.youtube) return
    const targetTime = PlayerLogic.seekPositionToTime(position, this.videoDurationValue, this.startTimeValue, this.endTimeValue, this.isSegmentMode())
    this.updateCurrentTimeLabel(targetTime)
    this.updateActiveSegment(targetTime)
    try {
      this.youtube.seekTo(targetTime, true)
    } catch (error) {
      console.error('Error seeking:', error)
    }
  }

  switchToFullMode() {
    this.segmentModeValue = false
    this.updateScrubberLabels()
  }

  updateDisabledZones() {
    const zones = PlayerLogic.disabledZones(this.startTimeValue, this.endTimeValue, this.videoDurationValue, this.isSegmentMode())
    if (this.hasScrubberPreDisabledTarget) {
      if (zones.preWidth !== null) this.scrubberPreDisabledTarget.style.width = zones.preWidth
      this.scrubberPreDisabledTarget.classList.toggle('hidden', zones.preHidden)
    }
    if (this.hasScrubberPostDisabledTarget) {
      if (zones.postWidth !== null) this.scrubberPostDisabledTarget.style.width = zones.postWidth
      this.scrubberPostDisabledTarget.classList.toggle('hidden', zones.postHidden)
    }
  }

  formatTime(seconds) {
    return PlayerLogic.formatTime(seconds)
  }

  // --- End scrubber helpers ---

  updateProgress(position) {
    // Course-progress is a lesson-context concern only. Watch (playback) and
    // onboarding (preview) contexts have no course/lesson to post against.
    if (this.isPlaybackMode() || this.isPreviewMode()) return

    const url = `/courses/${this.courseUuidValue}/lessons/${this.lessonIdValue}/update_progress`

    post(url, {
      responseKind: 'json',
      body: {
        position: position,
        watched_duration: 10
      }
    }).then(response => {
      if (response.ok) {
        response.json.then(data => {
          // Dispatch event for chapter navigation to update UI
          if (data.completed) {
            this.dispatch('lessonCompleted', { detail: { lessonId: this.lessonIdValue } })
          }

          // Update course progress bar if it exists
          this.dispatch('progressUpdated', {
            detail: {
              courseCompletion: data.course_completion_percentage
            }
          })
        })
      }
    })
  }

  handleWindowUnload() {
    // Watch context: persist the in-flight VideoPlayback range.
    if (this.isPlaybackMode()) {
      this.handlePlaybackUnload()
      return
    }

    // Preview context: nothing to persist (no progress, no analytics).
    if (this.isPreviewMode()) return

    // Lesson context: save progress on page unload
    if (this.youtube && this.youtube.getCurrentTime) {
      try {
        // YouTube iframe API getCurrentTime() is synchronous, not a Promise
        const time = this.youtube.getCurrentTime()

        if (typeof time === 'number' && time > 0) {
          const formData = new FormData()
          formData.append('position', time)
          formData.append('watched_duration', 10)

          const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content')
          if (csrfToken) {
            formData.append('authenticity_token', csrfToken)
          }

          const url = `/courses/${this.courseUuidValue}/lessons/${this.lessonIdValue}/update_progress`
          if (navigator.sendBeacon) {
            navigator.sendBeacon(url, formData)
          }
        }
      } catch (error) {
        // Silently fail during page unload to avoid console errors
        console.warn('Could not save progress on unload:', error)
      }
    }
  }

  // Playback speed controls
  setSpeed(speed) {
    this.playbackSpeed = speed
    if (this.youtube) {
      try {
        this.youtube.setPlaybackRate(speed)

        // Auto-mute at slow speeds (audio becomes inaudible/distorted)
        if (PlayerLogic.speedShouldMute(speed)) {
          this.youtube.mute()
        } else {
          this.youtube.unMute()
        }
      } catch (error) {
        console.error('Error setting playback rate:', error)
      }
    }
    this.updateSpeedDisplay()
  }

  // Generic speed selector — reads data-speed so the dropdown can offer any set
  // (currently 0.25–1.5) without a method per value.
  selectSpeed(event) {
    event.preventDefault()
    const speed = parseFloat(event.currentTarget.dataset.speed)
    if (!Number.isNaN(speed)) this.setSpeed(speed)
  }

  // --- Segment list (Phase 5) ---------------------------------------------

  // Jump to a segment's start (hot-linked segment list). Full-video playback —
  // segments are navigation, not capping.
  jumpToSegment(event) {
    event.preventDefault()
    if (!this.youtube) return
    const seconds = parseFloat(event.currentTarget.dataset.seconds)
    if (Number.isNaN(seconds)) return
    try {
      this.updateActiveSegment(seconds)
      this.youtube.seekTo(seconds, true)
      this.youtube.playVideo()
    } catch (error) {
      console.error('Error jumping to segment:', error)
    }
  }

  // Highlight the currently-playing segment row.
  updateActiveSegment(currentTime) {
    if (!this.hasSegmentRowTarget) return
    const segments = this.segmentRowTargets.map(row => ({
      start: parseFloat(row.dataset.seconds),
      end: parseFloat(row.dataset.endSeconds)
    }))
    const active = PlayerLogic.activeSegmentIndex(currentTime, segments)
    this.segmentRowTargets.forEach((row, i) => {
      row.classList.toggle('--active', i === active)
      row.setAttribute('aria-current', i === active ? 'true' : 'false')
    })

    if (active < 0) return

    const activeLessonId = this.segmentRowTargets[active].dataset.lessonId || null
    if (!activeLessonId) return
    if (activeLessonId !== this.relatedLessonId) {
      this.relatedLessonId = activeLessonId
      window.dispatchEvent(new CustomEvent('player-recommendations:lesson-change', {
        detail: { lessonId: activeLessonId }
      }))
    }
  }

  updateSpeedDisplay() {
    const label = `${this.playbackSpeed}x`

    // The collapsed dropdown trigger shows the current speed (one per player).
    this.currentSpeedDisplayTargets.forEach(el => {
      el.textContent = label
    })

    // Mark the active option inside the popover. Theme-neutral `--active` class
    // (amber) so it works on both the rafa and gray surfaces.
    this.speedButtonTargets.forEach(button => {
      const speed = parseFloat(button.dataset.speed)
      button.classList.toggle('--active', speed === this.playbackSpeed)
    })
  }

  // Skip controls
  skipForward(event) {
    event.preventDefault()
    if (this.youtube && this.youtube.getCurrentTime) {
      try {
        const time = this.youtube.getCurrentTime()
        this.youtube.seekTo(PlayerLogic.skipForwardTarget(time, this.endTimeValue, this.isSegmentMode()), true)
      } catch (error) {
        console.error('Error skipping forward:', error)
      }
    }
  }

  skipBackward(event) {
    event.preventDefault()
    if (this.youtube && this.youtube.getCurrentTime) {
      try {
        const time = this.youtube.getCurrentTime()
        this.youtube.seekTo(PlayerLogic.skipBackwardTarget(time, this.startTimeValue, this.isSegmentMode()), true)
      } catch (error) {
        console.error('Error skipping backward:', error)
      }
    }
  }

  // Play/Pause control
  togglePlayPause(event) {
    event.preventDefault()
    if (!this.youtube) return

    try {
      if (this.isPlaying) {
        this.youtube.pauseVideo()
      } else {
        this.youtube.playVideo()
      }
    } catch (error) {
      console.error('Error toggling play/pause:', error)
    }
  }

  // The mobile sheet can collapse its video chrome to give the supporting
  // content the full viewport. Collapsing is deliberately pause-only: restoring
  // the player never resumes media without another explicit user action.
  pauseForPopover() {
    if (!this.youtube || !this.youtube.pauseVideo) return

    try {
      this.youtube.pauseVideo()
    } catch (error) {
      console.error('Error pausing collapsed player:', error)
    }
  }

  updatePlayPauseButton() {
    if (!this.hasPlayPauseButtonTarget) return

    if (this.isPlaying) {
      // Show pause icon, hide play icon on all play/pause buttons
      this.playIconTargets.forEach(icon => icon.classList.add('hidden'))
      this.pauseIconTargets.forEach(icon => icon.classList.remove('hidden'))
      // Disable frame step buttons when playing (affects all frame step buttons)
      if (this.hasStepForwardButtonTarget) {
        this.stepForwardButtonTargets.forEach(btn => btn.disabled = true)
      }
      if (this.hasStepBackButtonTarget) {
        this.stepBackButtonTargets.forEach(btn => btn.disabled = true)
      }
    } else {
      // Show play icon, hide pause icon on all play/pause buttons
      this.playIconTargets.forEach(icon => icon.classList.remove('hidden'))
      this.pauseIconTargets.forEach(icon => icon.classList.add('hidden'))
      // Enable frame step buttons when paused (affects all frame step buttons)
      if (this.hasStepForwardButtonTarget) {
        this.stepForwardButtonTargets.forEach(btn => btn.disabled = false)
      }
      if (this.hasStepBackButtonTarget) {
        this.stepBackButtonTargets.forEach(btn => btn.disabled = false)
      }
    }
  }

  // Frame stepping (only works when paused)
  stepForward(event) {
    event.preventDefault()
    if (!this.youtube || this.isPlaying) return

    try {
      const time = this.youtube.getCurrentTime()
      this.youtube.seekTo(PlayerLogic.stepForwardTarget(time, this.startTimeValue, this.endTimeValue, this.isSegmentMode(), this.frameStep), true)
    } catch (error) {
      console.error('Error stepping forward:', error)
    }
  }

  stepBackward(event) {
    event.preventDefault()
    if (!this.youtube || this.isPlaying) return

    try {
      const time = this.youtube.getCurrentTime()
      this.youtube.seekTo(PlayerLogic.stepBackwardTarget(time, this.startTimeValue, this.endTimeValue, this.isSegmentMode(), this.frameStep), true)
    } catch (error) {
      console.error('Error stepping backward:', error)
    }
  }

  showCompletionInterstitial() {
    const interstitialModal = document.querySelector('[data-controller="lesson-interstitial"]')
    if (interstitialModal) {
      // Get the Stimulus controller instance and call show() method
      const stimulus = window.Stimulus || this.application
      const interstitialController = stimulus.getControllerForElementAndIdentifier(
        interstitialModal,
        'lesson-interstitial'
      )

      if (interstitialController) {
        interstitialController.show()
      } else {
        // Fallback if controller not found
        interstitialModal.classList.remove('hidden')
        document.body.style.overflow = 'hidden'
      }
    }
  }

  handleReplay(event) {
    if (this.youtube) {
      // Restore segment mode if this lesson has a segment
      if (this.endTimeValue > 0) {
        this.segmentModeValue = true
        this.updateScrubberLabels()
      }
      this.youtube.seekTo(this.startTimeValue || 0, true)
      this.youtube.playVideo()
      this.isPlaying = true
      this.updatePlayPauseButton()
    }
  }

  handleWatchFull(event) {
    if (this.youtube) {
      this.switchToFullMode()
      this.youtube.seekTo(0, true)
      this.youtube.playVideo()
      this.isPlaying = true
      this.updatePlayPauseButton()
    }
  }

  get player() {
    return this.youtube
  }

  // True once initializePlayer has decided to offer the Resume/Start overlay.
  // In that case the video is cued (not autoplayed) and the overlay owns the
  // first play — callers (e.g. the intro modal) must not force playback.
  get isOfferingResume() {
    return Boolean(this.resume && this.resume.offer)
  }
}
